/**
 * `Atom` - the one-value subscribable everything reactive in this app is built
 * from. It lives in its own module rather than in `store.ts` so that modules
 * `store.ts` itself depends on - `feature-flags.ts`, which it asks about `live`
 * while creating its own atoms - can hold an atom without the two files forming
 * an import cycle that evaluates half-built at startup.
 */

type Listener<T> = (value: T) => void;

export class Atom<T> {
  private listeners = new Set<Listener<T>>();
  constructor(private current: T) {}

  get value(): T {
    return this.current;
  }

  set(next: T): void {
    if (Object.is(this.current, next)) return;
    this.current = next;
    for (const fn of this.listeners) fn(next);
  }

  /** Like `set` but always notifies - for collections mutated in place. */
  notify(): void {
    for (const fn of this.listeners) fn(this.current);
  }

  update(fn: (prev: T) => T): void {
    this.set(fn(this.current));
  }

  subscribe(fn: Listener<T>): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }
}
